/**
 * @fileoverview Ad Spends page wiring FilterProvider, PageMeta, and AdSpendsComponent.
 * @module features/ad-spends/pages/AdSpendsPage
 */

import { ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFilterMenuSlot } from '@/shared/contexts/FilterMenuSlotContext'
import PageMeta from '@/shared/components/common/PageMeta'
import { AdSpendsComponent } from '@/features/ad-spends/components/AdSpendsComponents'
import { type Platform } from '@/features/ad-spends/schemas/adSpendsSchema'
import { AdSpendsFiltersExtension } from '@/shared/components/filters/extensions/AdSpendsFiltersExtension'

/**
 * AdSpendsPage: top-level page for Ad Spends with context, meta, filters and table.
 *
 * @component
 * @example
 * ```tsx
 * <AdSpendsPage />
 * ```
 */
export function AdSpendsPage(): ReactElement {
  const [platforms, setPlatforms] = useState<Platform[]>([])
  const { setSlot, clearSlot } = useFilterMenuSlot()
  const { t } = useTranslation('features-ad-spends')

  useEffect(() => {
    setSlot({
      content: <AdSpendsFiltersExtension platforms={platforms} onPlatformsChange={setPlatforms} />,
      hideAggregation: true,
      hideProfitBasis: true,
      maxWidthClass: 'max-w-screen-2xl',
    })
    return () => clearSlot()
  }, [platforms, setSlot, clearSlot])

  return (
    <>
      <PageMeta title={t('title')} description={t('subtitle')} />
      <div className="min-h-screen bg-card">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-8 space-y-6">
          {/* Page header */}
          <div className="p-6 pb-3 flex items-center justify-between bg-card rounded-lg">
            <h1 className="text-2xl font-bold text-foreground">{t('title')}</h1>
          </div>

          {/* Main content card */}
          <div className="px-6 pb-6 bg-card rounded-3xl border border-border/15 flex-1 flex flex-col">
            <AdSpendsComponent platforms={platforms} />
          </div>
        </div>
      </div>
    </>
  )
}
